import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Sparkles, AlertCircle } from "lucide-react";
import { api, ApiError } from "../api";
import type { CreateSandboxRequest, EngineName } from "../types";

const EXAMPLE_PROMPTS = [
  "ecommerce with 50 users, 200 orders, 30 products",
  "github clone — users, repos, issues, pull requests",
  "analytics events table with 10k rows over 30 days",
  "pokemon database with types and abilities",
];

export function CreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [engine, setEngine] = useState<EngineName>("postgres");
  const [prompt, setPrompt] = useState("");
  const [ttl, setTtl] = useState(3600);

  const mutation = useMutation({
    mutationFn: (req: CreateSandboxRequest) => api.createSandbox(req),
    onSuccess: (sandbox) => {
      queryClient.invalidateQueries({ queryKey: ["sandboxes"] });
      navigate(`/sandbox/${encodeURIComponent(sandbox.id)}`);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    mutation.mutate({ engine, prompt: prompt.trim(), ttl_seconds: ttl });
  };

  const ttlLabel = formatTtl(ttl);

  return (
    <div className="max-w-3xl">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink-400 hover:text-phosphor-300 mb-6"
      >
        <ArrowLeft size={12} />
        back to sandboxes
      </Link>

      <div className="mb-10">
        <div className="term-label mb-3">new</div>
        <h1 className="font-serif text-5xl text-ink-100 leading-[0.95]">
          provision a <em className="text-phosphor-400">sandbox.</em>
        </h1>
        <p className="mt-4 text-sm text-ink-400 max-w-xl">
          Describe the database you want in plain English. We&apos;ll spin up a fresh
          Postgres instance, seed it, and hand you a connection string.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-8">
        <Section label="01 / engine">
          <div className="grid grid-cols-3 gap-2">
            <EngineOption
              name="postgres"
              selected={engine === "postgres"}
              onClick={() => setEngine("postgres")}
              available
            />
            <EngineOption name="mysql" selected={false} onClick={() => {}} />
            <EngineOption name="sqlite" selected={false} onClick={() => {}} />
          </div>
        </Section>

        <Section label="02 / prompt">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={6}
            placeholder="e.g. ecommerce with 50 users, 200 orders, 30 products across 5 categories"
            className="input font-mono !text-[13px] leading-relaxed"
            autoFocus
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="text-[10px] uppercase tracking-widest text-ink-500 mr-1 self-center">
              try
            </span>
            {EXAMPLE_PROMPTS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPrompt(p)}
                className="text-[11px] text-ink-400 hover:text-phosphor-300 border border-ink-700 hover:border-phosphor-600 bg-ink-850 px-2 py-1 transition-colors"
              >
                {p}
              </button>
            ))}
          </div>
        </Section>

        <Section label="03 / ttl">
          <div className="bracket-frame !bg-ink-900">
            <span className="bracket-bl" />
            <span className="bracket-br" />
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-ink-400 mb-1">
                  auto-destroy in
                </div>
                <div className="font-serif text-3xl text-phosphor-400">
                  {ttlLabel}
                </div>
              </div>
              <div className="text-right text-[10px] uppercase tracking-widest text-ink-500">
                <div>min 60s</div>
                <div>max 24h</div>
              </div>
            </div>
            <input
              type="range"
              min={60}
              max={86400}
              step={60}
              value={ttl}
              onChange={(e) => setTtl(Number(e.target.value))}
              className="w-full accent-[var(--color-phosphor-400)]"
            />
            <div className="mt-3 flex gap-2">
              {[
                [300, "5m"],
                [900, "15m"],
                [3600, "1h"],
                [14400, "4h"],
                [86400, "24h"],
              ].map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setTtl(Number(v))}
                  className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${
                    ttl === v
                      ? "border-phosphor-400 text-phosphor-300 bg-phosphor-800/20"
                      : "border-ink-700 text-ink-400 hover:border-ink-600"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </Section>

        {mutation.error && (
          <div className="border border-danger-900 bg-danger-900/20 p-4 flex gap-3">
            <AlertCircle className="text-danger-400 shrink-0 mt-0.5" size={16} />
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-danger-400">
                provisioning failed
              </div>
              <pre className="text-xs text-ink-100 whitespace-pre-wrap break-words">
                {mutation.error instanceof ApiError
                  ? mutation.error.message
                  : String(mutation.error)}
              </pre>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-ink-800">
          <p className="text-[11px] text-ink-500">
            destroys automatically · takes ~10–30s · no persistence
          </p>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={mutation.isPending || !prompt.trim()}
          >
            {mutation.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                provisioning…
              </>
            ) : (
              <>
                <Sparkles size={14} />
                spin it up
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="text-[10px] uppercase tracking-[0.2em] text-ink-500 mb-3 flex items-center gap-2">
        <span className="text-phosphor-400">{label.split(" ")[0]}</span>
        <span className="text-ink-700">/</span>
        <span>{label.split("/").slice(1).join("/").trim()}</span>
      </div>
      {children}
    </section>
  );
}

function EngineOption({
  name,
  selected,
  onClick,
  available = false,
}: {
  name: string;
  selected: boolean;
  onClick: () => void;
  available?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!available}
      className={`relative text-left px-4 py-3 border transition-all ${
        selected
          ? "border-phosphor-400 bg-phosphor-800/20 text-phosphor-300"
          : available
            ? "border-ink-700 bg-ink-900 text-ink-200 hover:border-ink-500"
            : "border-ink-800 bg-ink-950 text-ink-600 cursor-not-allowed"
      }`}
    >
      <div className="font-mono text-sm font-medium lowercase">{name}</div>
      <div className="text-[10px] uppercase tracking-widest mt-1 opacity-70">
        {available ? (selected ? "selected" : "available") : "soon"}
      </div>
      {selected && (
        <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-phosphor-400 shadow-[0_0_6px_var(--color-phosphor-400)]" />
      )}
    </button>
  );
}

function formatTtl(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) {
    const h = seconds / 3600;
    return h % 1 === 0 ? `${h}h` : `${h.toFixed(1)}h`;
  }
  const d = seconds / 86400;
  return d % 1 === 0 ? `${d}d` : `${d.toFixed(1)}d`;
}
